/**
 * Centralized SEO Configuration for All Pages
 * Provides route-specific meta titles, descriptions, keywords, OpenGraph,
 * Twitter cards, canonical links, and Schema.org structured data.
 */

export const DEFAULT_SEO = {
  title: "Simpel — The Cognitive AI Company",
  description:
    "Simpel transforms enterprise workflows into intelligent, autonomous cognitive systems. Empowering organizations with AI-driven operations, digital workers, and industry copilots.",
  keywords: [
    "Cognitive AI",
    "Enterprise AI",
    "AI Copilot",
    "Digital Workers",
    "Autonomous AI",
    "Process Automation",
    "Simpel AI",
  ],
  canonical: "https://simpel.ai",
  ogType: "website",
  ogImage: "https://simpel.ai/images/simpel-icon-mark.png",
  robots: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
};

export const ROUTE_SEO_MAP = {
  "/": {
    title: "Simpel — The Cognitive AI Company | Enterprise Automation Simplified",
    description:
      "Transforming enterprise workflows into intelligent cognitive systems. Simpel delivers industry-specific copilots, autonomous digital workers, and full-stack operational intelligence.",
    keywords: [
      "Simpel AI",
      "Cognitive AI Company",
      "Enterprise Automation",
      "Autonomous AI",
      "Industry AI Copilots",
      "Digital Workers",
    ],
    canonical: "/",
  },

  "/what-we-do": {
    title: "What We Do | Full-Stack Cognitive AI & Digital Transformation | Simpel",
    description:
      "Explore Simpel's end-to-end cognitive solutions: Agentic AI, Legacy Data Migration, Automation Audits, Automated Workflows, Digital Workers, and ERP Implementation.",
    keywords: [
      "Cognitive AI Solutions",
      "Enterprise AI Services",
      "Digital Transformation",
      "AI Workflows",
      "Agentic AI",
    ],
    canonical: "/what-we-do",
  },

  "/agentic-ai": {
    title: "Agentic AI | Autonomous Multi-Agent Systems for Enterprise | Simpel",
    description:
      "Deploy autonomous Agentic AI that plans, decides, and executes complex enterprise tasks across systems with enterprise-grade guardrails and visibility.",
    keywords: [
      "Agentic AI",
      "Autonomous AI Agents",
      "Enterprise Multi-Agent Systems",
      "AI Task Execution",
    ],
    canonical: "/agentic-ai",
  },

  "/services/agentic-ai": {
    title: "Agentic AI Services | Simpel",
    canonical: "/agentic-ai",
  },

  "/services/legacy-data-migration": {
    title: "Legacy Data Migration | AI-Powered Data Extraction & Modernization | Simpel",
    description:
      "Modernize legacy databases and enterprise systems with zero business disruption using Simpel's intelligent, AI-assisted data migration pipelines.",
    keywords: [
      "Legacy Data Migration",
      "Database Modernization",
      "ERP Data Migration",
      "AI ETL",
    ],
    canonical: "/services/legacy-data-migration",
  },

  "/services/automation-audit": {
    title: "Automation Audit | Identify High-Impact AI Opportunities | Simpel",
    description:
      "Discover bottlenecks, measure automation ROI, and build a strategic AI roadmap with Simpel's comprehensive enterprise automation audit.",
    keywords: [
      "Automation Audit",
      "Process Mining AI",
      "Enterprise AI Assessment",
      "Automation ROI",
    ],
    canonical: "/services/automation-audit",
  },

  "/services/automated-workflows": {
    title: "Automated Workflows | End-to-End Process Orchestration | Simpel",
    description:
      "Eliminate repetitive manual tasks. Connect fragmented enterprise software and automate end-to-end operational workflows with cognitive intelligence.",
    keywords: [
      "Automated Workflows",
      "Process Orchestration",
      "Workflow Automation",
      "Enterprise RPA",
    ],
    canonical: "/services/automated-workflows",
  },

  "/services/digital-workers": {
    title: "Digital Workers | Scalable AI Agents for Operations & Back-Office | Simpel",
    description:
      "Scale operations 24/7 with autonomous digital workers that handle customer requests, data entry, reconciliation, compliance, and reporting.",
    keywords: [
      "Digital Workers",
      "AI Workforce",
      "Virtual Workers",
      "Back-Office Automation",
    ],
    canonical: "/services/digital-workers",
  },

  "/services/erp-implementations": {
    title: "ERP Implementations | Intelligent, Agile ERP Deployment | Simpel",
    description:
      "Fast-track ERP deployment and adoption. Integrate planning, inventory, sales, and manufacturing with Simpel's modern AI-first ERP methodology.",
    keywords: [
      "ERP Implementation",
      "Cloud ERP Services",
      "Enterprise ERP Deployment",
      "AI ERP Integration",
    ],
    canonical: "/services/erp-implementations",
  },

  "/stack/manufacturing-automation": {
    title: "Manufacturing Automation Stack | Unified Industrial Intelligence | Simpel",
    description:
      "From shop-floor sensors and MES to predictive maintenance and enterprise planning: Simpel's unified manufacturing intelligence stack lifts yield and cuts downtime.",
    keywords: [
      "Manufacturing Automation Stack",
      "Industrial AI",
      "Smart Factory",
      "MES AI",
      "Predictive Maintenance",
    ],
    canonical: "/stack/manufacturing-automation",
  },

  "/stack/sales-automation": {
    title: "Sales Automation Stack | AI-Powered Revenue Operations | Simpel",
    description:
      "Supercharge revenue teams with automated lead intelligence, relationship copilots, CRM enrichment, and predictive pipeline analytics.",
    keywords: [
      "Sales Automation Stack",
      "Revenue Intelligence",
      "AI CRM Copilot",
      "Sales AI",
    ],
    canonical: "/stack/sales-automation",
  },

  "/products/ek-rewards": {
    title: "Ek Rewards | AI-Powered Customer Loyalty & Engagement Platform | Simpel",
    description:
      "Drive customer retention, personalized rewards, and omnichannel engagement with Ek Rewards by Simpel.",
    keywords: ["Customer Loyalty AI", "Rewards Platform", "Ek Rewards", "Retention AI"],
    canonical: "/products/ek-rewards",
  },

  "/products/xp-skills": {
    title: "XP Skills | Workforce Upskilling & Talent Intelligence | Simpel",
    description:
      "Empower employees with adaptive, AI-driven learning paths, skill gap analysis, and real-time operational training with XP Skills.",
    keywords: ["Workforce AI", "Upskilling Platform", "XP Skills", "Employee Learning AI"],
    canonical: "/products/xp-skills",
  },

  "/products/simpel-erp": {
    title: "Simpel ERP | Next-Generation Cognitive Enterprise Resource Planning",
    description:
      "Modern, cloud-native ERP built with integrated cognitive AI for manufacturing, supply chain, financial management, and multi-entity operations.",
    keywords: ["Cognitive ERP", "Simpel ERP", "Cloud ERP", "Manufacturing ERP AI"],
    canonical: "/products/simpel-erp",
  },

  "/ai-opportunities": {
    title: "AI Opportunities Explorer | Industry Copilots & Functional AI | Simpel",
    description:
      "Explore high-impact Cognitive AI opportunities across Manufacturing, Financial Services, Healthcare, Government, and Professional Services.",
    keywords: [
      "AI Opportunities",
      "Cognitive AI Use Cases",
      "Industry AI Copilots",
      "Enterprise AI Calculator",
    ],
    canonical: "/ai-opportunities",
  },

  "/cognitive": {
    title: "Cognitive AI | Industry-Specific Intelligence Copilots | Simpel",
    description:
      "Discover Simpel's suite of specialized cognitive copilots built to solve deep domain problems in manufacturing, healthcare, finance, and public services.",
    keywords: [
      "Cognitive AI",
      "Domain Copilots",
      "Specialized AI Systems",
      "Enterprise Intelligence",
    ],
    canonical: "/cognitive",
  },

  "/cognitive/industries/financial-services/relationship-manager-copilot": {
    title: "Relationship Manager Copilot | Financial Services Intelligence | Simpel",
    description:
      "Help relationship managers prepare faster, personalize client conversations, and deepen trust with instant 360-degree financial intelligence.",
    keywords: [
      "Relationship Manager Copilot",
      "Wealth Management AI",
      "Banking AI Copilot",
      "Financial Services AI",
    ],
    canonical: "/cognitive/industries/financial-services/relationship-manager-copilot",
    ogImage: "/images/insights/rm-copilot-hero.jpg",
  },

  "/cognitive/industries/manufacturing/quality-intelligence": {
    title: "Quality Intelligence | First-Pass Yield & Defect Reduction | Simpel",
    description:
      "Correlate production parameters with inspection data in real time. Detect defect patterns earlier and lift first-pass yield with Simpel Quality Intelligence.",
    keywords: [
      "Quality Intelligence",
      "Manufacturing Yield AI",
      "Defect Detection AI",
      "Factory Quality Control",
    ],
    canonical: "/cognitive/industries/manufacturing/quality-intelligence",
    ogImage: "/images/insights/quality-intelligence-hero.jpg",
  },

  "/cognitive/industries/government-public-services/citizen-services-assistant": {
    title: "Citizen Services Assistant | Government & Public Sector AI | Simpel",
    description:
      "Guide citizens through public schemes, forms, and services with plain-language AI self-service, reducing contact center volume by 20–35%.",
    keywords: [
      "Citizen Services Assistant",
      "Public Sector AI",
      "Government Citizen Portal",
      "Public Services Self-Service",
    ],
    canonical: "/cognitive/industries/government-public-services/citizen-services-assistant",
    ogImage: "/images/insights/citizen-services-hero.jpg",
  },

  "/cognitive/industries/government-public-services/government-knowledge-copilot": {
    title: "Government Knowledge Copilot | Public Sector AI | Simpel",
    description:
      "Connect policies, regulations, circulars, and institutional knowledge so public-sector teams find relevant information faster with Simpel Government Knowledge Copilot.",
    keywords: [
      "Government Knowledge Copilot",
      "Public Sector Knowledge Management",
      "Government Policy AI",
      "Public Sector AI",
      "Institutional Knowledge Assistant",
    ],
    canonical: "/cognitive/industries/government-public-services/government-knowledge-copilot",
    ogImage: "/images/insights/government-knowledge-hero.jpg",
  },

  "/case-studies": {
    title: "Case Studies & Proven Impact | Real-World Enterprise Results | Simpel",
    description:
      "See how leading enterprises achieved 40-60% faster cycle times, 25-45% self-service lift, and measurable multi-million dollar ROI with Simpel Cognitive AI.",
    keywords: [
      "AI Case Studies",
      "Enterprise Automation Results",
      "Cognitive AI ROI",
      "Manufacturing AI Case Study",
    ],
    canonical: "/case-studies",
  },

  "/case-studies/skipper-limited": {
    title: "Skipper Limited Case Study | Digital Supply Chain & Plant Automation | Simpel",
    description:
      "How Skipper Limited transformed manufacturing and distribution operations using Simpel's end-to-end automation stack.",
    keywords: ["Skipper Limited Case Study", "Plant Automation", "Industrial AI Results"],
    canonical: "/case-studies/skipper-limited",
  },

  "/labs": {
    title: "Simpel Labs | Next-Gen AI Research, Sandbox & Innovations | Simpel",
    description:
      "Behind the scenes at Simpel Labs: Exploring frontier multimodal architectures, physical AI, real-time edge processing, and cognitive reasoning.",
    keywords: ["Simpel Labs", "AI R&D", "Frontier AI", "Physical AI Research"],
    canonical: "/labs",
  },

  "/insights": {
    title: "Insights & Thought Leadership | The Future of Cognitive AI | Simpel",
    description:
      "In-depth research, articles, and executive briefs on cognitive architectures, enterprise automation trends, and agentic workflows.",
    keywords: ["AI Insights", "Cognitive AI Blog", "Enterprise AI Trends"],
    canonical: "/insights",
  },

  "/about": {
    title: "About Us | Simpel — The Cognitive AI Company",
    description:
      "Learn about Simpel's mission to make enterprise operations simple, autonomous, and cognitive through human-centered artificial intelligence.",
    keywords: ["About Simpel", "Simpel AI Team", "Cognitive AI Leadership"],
    canonical: "/about",
  },

  "/careers": {
    title: "Careers at Simpel | Build the Future of Cognitive AI",
    description:
      "Join world-class AI engineers, systems architects, and product builders at Simpel. Explore open roles in machine learning, full-stack, and agentic systems.",
    keywords: ["AI Careers", "Machine Learning Jobs", "Simpel Careers", "Work in AI"],
    canonical: "/careers",
  },

  "/careers/open-positions": {
    title: "Open Positions | Join the Simpel Team",
    description:
      "View all current job openings at Simpel across engineering, product, sales, and AI research.",
    keywords: ["Simpel Jobs", "AI Engineer Openings", "Tech Hiring"],
    canonical: "/careers/open-positions",
  },

  "/privacy-policy": {
    title: "Privacy Policy | Simpel",
    description:
      "Read Simpel's Privacy Policy to understand how we collect, use, and protect your personal and enterprise data.",
    canonical: "/privacy-policy",
    robots: "noindex, follow",
  },

  "/terms-of-use": {
    title: "Terms of Use | Simpel",
    description:
      "Terms of Use and service agreements governing the use of Simpel products, websites, and cognitive AI platforms.",
    canonical: "/terms-of-use",
    robots: "noindex, follow",
  },

  "/cookie-policy": {
    title: "Cookie Policy | Simpel",
    description:
      "Information about how Simpel uses cookies and similar technologies on our website and digital services.",
    canonical: "/cookie-policy",
    robots: "noindex, follow",
  },
};

